import React, { useState, useEffect } from 'react';
import * as Icons from 'lucide-react';
import { X, Eye, Download, ShieldCheck, FileCheck } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import useReveal from '../hooks/useReveal';
import { CERT_ICONS, IMAGES } from '../mock/mock';
import { PageHero } from './Services';

const CERT_PDF = '/certificates/arge-gmp-iso.pdf';

const CERTIFICATES = [
  { std: 'ISO 9001:2015', no: 'QMS-26.04.356', img: '/certificates/cert-1.jpg' },
  { std: 'ISO 14001:2015', no: 'EMS-26.04.356', img: '/certificates/cert-2.jpg' },
  { std: 'ISO 45001:2018', no: 'ISG-26.04.356', img: '/certificates/cert-3.jpg' },
  { std: 'ISO 22716:2013', no: 'CGM-26.04.356', img: '/certificates/cert-4.jpg' },
];

const DOCS_I18N = {
  en: { title: 'Official Certificates', subtitle: 'Independently audited and accredited by DSR Certification (IAF / IAS). Valid through 27 April 2027.', view: 'View', download: 'Download PDF', all: 'Download all certificates (PDF)', certNo: 'Certificate No', close: 'Close', names: ['Quality Management System', 'Environmental Management System', 'Occupational Health & Safety', 'Cosmetics Good Manufacturing Practice (GMP)'] },
  fr: { title: 'Certificats Officiels', subtitle: 'Audité et accrédité de façon indépendante par DSR Certification (IAF / IAS). Valable jusqu’au 27 avril 2027.', view: 'Voir', download: 'Télécharger le PDF', all: 'Télécharger tous les certificats (PDF)', certNo: 'N° de certificat', close: 'Fermer', names: ['Système de management de la qualité', 'Système de management environnemental', 'Santé et sécurité au travail', 'Bonnes pratiques de fabrication cosmétique (BPF)'] },
  es: { title: 'Certificados Oficiales', subtitle: 'Auditado y acreditado de forma independiente por DSR Certification (IAF / IAS). Válido hasta el 27 de abril de 2027.', view: 'Ver', download: 'Descargar PDF', all: 'Descargar todos los certificados (PDF)', certNo: 'N.º de certificado', close: 'Cerrar', names: ['Sistema de gestión de calidad', 'Sistema de gestión ambiental', 'Seguridad y salud en el trabajo', 'Buenas prácticas de fabricación de cosméticos (BPF)'] },
  de: { title: 'Offizielle Zertifikate', subtitle: 'Unabhängig auditiert und akkreditiert durch DSR Certification (IAF / IAS). Gültig bis 27. April 2027.', view: 'Ansehen', download: 'PDF herunterladen', all: 'Alle Zertifikate herunterladen (PDF)', certNo: 'Zertifikat-Nr.', close: 'Schließen', names: ['Qualitätsmanagementsystem', 'Umweltmanagementsystem', 'Arbeitsschutz-Managementsystem', 'Gute Herstellungspraxis für Kosmetik (GMP)'] },
  ar: { title: 'الشهادات الرسمية', subtitle: 'تم تدقيقها واعتمادها بشكل مستقل من DSR Certification (IAF / IAS). صالحة حتى 27 أبريل 2027.', view: 'عرض', download: 'تنزيل PDF', all: 'تنزيل جميع الشهادات (PDF)', certNo: 'رقم الشهادة', close: 'إغلاق', names: ['نظام إدارة الجودة', 'نظام الإدارة البيئية', 'الصحة والسلامة المهنية', 'ممارسات التصنيع الجيدة لمستحضرات التجميل (GMP)'] },
  hi: { title: 'आधिकारिक प्रमाणपत्र', subtitle: 'DSR Certification (IAF / IAS) द्वारा स्वतंत्र रूप से ऑडिट व मान्यता प्राप्त। 27 अप्रैल 2027 तक वैध।', view: 'देखें', download: 'PDF डाउनलोड करें', all: 'सभी प्रमाणपत्र डाउनलोड करें (PDF)', certNo: 'प्रमाणपत्र संख्या', close: 'बंद करें', names: ['गुणवत्ता प्रबंधन प्रणाली', 'पर्यावरण प्रबंधन प्रणाली', 'व्यावसायिक स्वास्थ्य एवं सुरक्षा', 'कॉस्मेटिक्स गुड मैन्युफैक्चरिंग प्रैक्टिस (GMP)'] },
  tr: { title: 'Resmi Sertifikalar', subtitle: 'DSR Certification (IAF / IAS) tarafından bağımsız olarak denetlenmiş ve akredite edilmiştir. 27 Nisan 2027’ye kadar geçerlidir.', view: 'Görüntüle', download: 'PDF İndir', all: 'Tüm sertifikaları indir (PDF)', certNo: 'Sertifika No', close: 'Kapat', names: ['Kalite Yönetim Sistemi', 'Çevre Yönetim Sistemi', 'İş Sağlığı ve Güvenliği', 'Kozmetik İyi Üretim Uygulamaları (GMP)'] },
  pt: { title: 'Certificados Oficiais', subtitle: 'Auditado e acreditado de forma independente pela DSR Certification (IAF / IAS). Válido até 27 de abril de 2027.', view: 'Ver', download: 'Descarregar PDF', all: 'Descarregar todos os certificados (PDF)', certNo: 'N.º de certificado', close: 'Fechar', names: ['Sistema de gestão da qualidade', 'Sistema de gestão ambiental', 'Saúde e segurança ocupacional', 'Boas práticas de fabrico de cosméticos (BPF)'] },
};

const Certifications = () => {
  const { t, lang } = useLang();
  useReveal(lang);
  const items = t('certs.items');
  const d = DOCS_I18N[lang] || DOCS_I18N.en;
  const [active, setActive] = useState(null);

  useEffect(() => {
    if (active === null) return;
    const onKey = (e) => { if (e.key === 'Escape') setActive(null); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [active]);

  return (
    <div>
      <PageHero kicker={t('certs.kicker')} title={t('certs.title')} subtitle={t('certs.subtitle')} image={IMAGES.manufacturing} />

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid sm:grid-cols-2 gap-8">
          {items.map((c, i) => {
            const Icon = Icons[CERT_ICONS[i]] || Icons.Award;
            return (
              <div key={i} className="reveal hover-lift bg-cream p-10 border-t-2 border-gold" style={{ transitionDelay: `${(i % 2) * 80}ms` }}>
                <div className="w-16 h-16 flex items-center justify-center bg-espresso text-gold-light mb-6"><Icon className="w-8 h-8" /></div>
                <h3 className="font-serif text-3xl text-espresso mb-3">{c.title}</h3>
                <p className="text-mocha leading-relaxed">{c.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Official certificate documents */}
      <section className="py-24 bg-cream" data-testid="certificates-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="reveal max-w-2xl mb-14">
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-10 bg-gold" />
              <span className="text-xs tracking-[0.25em] uppercase text-gold">{t('certs.kicker')}</span>
            </div>
            <h2 className="font-serif text-4xl md:text-5xl text-espresso mb-4">{d.title}</h2>
            <p className="text-mocha leading-relaxed">{d.subtitle}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {CERTIFICATES.map((c, i) => (
              <div key={c.no} className="reveal group bg-white border border-gold-soft shadow-sm hover:shadow-xl transition-shadow duration-400" style={{ transitionDelay: `${(i % 4) * 70}ms` }} data-testid={`certificate-card-${i}`}>
                <button
                  type="button"
                  onClick={() => setActive(i)}
                  aria-label={`${d.view} ${c.std}`}
                  data-testid={`certificate-view-${i}`}
                  className="relative block w-full overflow-hidden bg-[#f3efe6]"
                >
                  <img src={c.img} alt={`${c.std} — ${d.names[i]}`} loading="lazy" width="850" height="1167" className="w-full h-64 object-cover object-top transition-transform duration-500 group-hover:scale-105" />
                  <span className="absolute inset-0 bg-espresso/0 group-hover:bg-espresso/30 transition-colors duration-400 flex items-center justify-center">
                    <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 inline-flex items-center gap-2 px-4 py-2 bg-ivory/95 text-espresso text-xs tracking-[0.15em] uppercase">
                      <Eye className="w-4 h-4" /> {d.view}
                    </span>
                  </span>
                </button>
                <div className="p-5">
                  <div className="flex items-center gap-2 text-gold mb-1"><ShieldCheck className="w-4 h-4" /><span className="font-serif text-xl text-espresso">{c.std}</span></div>
                  <p className="text-sm text-mocha leading-snug min-h-[40px]">{d.names[i]}</p>
                  <p className="text-[0.7rem] tracking-[0.12em] uppercase text-mocha/70 mt-2">{d.certNo}: {c.no}</p>
                  <div className="flex gap-2 mt-4">
                    <button type="button" onClick={() => setActive(i)} data-testid={`certificate-view-btn-${i}`} className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 border border-espresso/25 text-espresso text-[0.68rem] tracking-[0.12em] uppercase hover:border-gold hover:text-gold transition-colors">
                      <Eye className="w-3.5 h-3.5" /> {d.view}
                    </button>
                    <a href={CERT_PDF} target="_blank" rel="noopener noreferrer" download data-testid={`certificate-download-${i}`} aria-label={d.download} className="inline-flex items-center justify-center gap-2 px-3 py-2 bg-espresso text-ivory text-[0.68rem] tracking-[0.12em] uppercase hover:bg-gold transition-colors">
                      <Download className="w-3.5 h-3.5" />
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="reveal mt-12 flex justify-center">
            <a href={CERT_PDF} target="_blank" rel="noopener noreferrer" download data-testid="download-all-certificates" className="inline-flex items-center gap-3 px-8 py-4 bg-espresso text-ivory text-xs tracking-[0.18em] uppercase hover:bg-gold transition-colors duration-400">
              <FileCheck className="w-5 h-5" /> {d.all}
            </a>
          </div>
        </div>
      </section>

      {/* Trust band */}
      <section className="py-16 bg-espresso">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-wrap items-center justify-center gap-x-14 gap-y-6 text-center">
          {['GMP', 'ISO 22716', 'ISO 9001', 'ISO 14001', 'ISO 45001'].map((b) => (
            <span key={b} className="font-serif text-2xl md:text-3xl text-ivory/40 tracking-wide">{b}</span>
          ))}
        </div>
      </section>

      {/* Lightbox */}
      {active !== null && (
        <div className="fixed inset-0 z-[60] bg-espresso/90 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8" onClick={() => setActive(null)} role="dialog" aria-modal="true" data-testid="certificate-lightbox">
          <div className="relative max-w-3xl w-full max-h-full flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between text-ivory mb-3">
              <div>
                <p className="font-serif text-2xl">{CERTIFICATES[active].std}</p>
                <p className="text-sm text-ivory/70">{d.names[active]}</p>
              </div>
              <button type="button" onClick={() => setActive(null)} aria-label={d.close} data-testid="certificate-lightbox-close" className="p-2 text-ivory/80 hover:text-gold transition-colors"><X className="w-7 h-7" /></button>
            </div>
            <div className="overflow-auto bg-white shadow-2xl">
              <img src={CERTIFICATES[active].img} alt={`${CERTIFICATES[active].std} — ${d.names[active]}`} className="w-full h-auto" />
            </div>
            <div className="flex justify-center mt-4">
              <a href={CERT_PDF} target="_blank" rel="noopener noreferrer" download className="inline-flex items-center gap-2 px-6 py-3 bg-gold text-espresso text-xs tracking-[0.15em] uppercase hover:bg-gold-light transition-colors">
                <Download className="w-4 h-4" /> {d.download}
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Certifications;
